import { VuexModule, Module, Mutation, Action } from 'vuex-module-decorators';
import { AppStoreModule } from '@/store';
import { TenantUser } from '@/common/models';
import IAdminService from '@/services/http/interfaces/IAdminService';
import { UsersForm } from '@/common/viewModels';
import { Validator, ValidationError } from 'class-validator';
import { UsersHelper } from '@/common/helpers/UsersHelper';

export interface IUsersState {
  users: TenantUser[];
  filter: string;
  currentPage: number;
  form: UsersForm;
  tenantUserInviteError: string;
}

@Module({
  name: 'users',
  namespaced: true,
})

export default class UsersStore extends VuexModule implements IUsersState {

  // State
  users: TenantUser[] = [];
  filter: string = '';
  currentPage = 0;
  form: UsersForm = new UsersForm();
  tenantUserInviteError = '';

  // Getters
  get filteredUsers(): TenantUser[] {
    const filterAsLowercase: string = this.filter.toLowerCase();

    return this.users.filter((t) =>
      (t.name && t.name.toLowerCase().includes(filterAsLowercase)) ||
      (t.email && t.email.toLowerCase().includes(filterAsLowercase)));
  }

  get isInviteUserFormValid(): boolean {
    return this.form.state.tenantUserInvite.isValid;
  }

  get inviteUserEmail(): string {
    return this.form.data.tenantUserInvite.email;
  }

  // Mutations
  @Mutation
  SET_TENANT_USER(users: TenantUser[]) {
    this.users = users;
  }

  @Mutation
  SET_FILTER(filter: string) {
    this.currentPage = 0;
    this.filter = filter;
  }

  @Mutation
  SET_CURRENT_PAGE(currentPage: number) {
    this.currentPage = currentPage;
  }

  @Mutation
  UPDATE_INVITE_USER_FORM_FIELD_BY_NAME(payload: { fieldName: string, value: any }) {
    (this.form.data.tenantUserInvite as any)[payload.fieldName] = payload.value;
  }

  @Mutation
  async UPDATE_INVITE_USER_FORM_STATE() {
    this.form.state.tenantUserInvite.validate(this.form.doValidation, this.form.data.tenantUserInvite);
  }

  @Mutation
  SET_INVITE_USER_FORM_DO_VALIDATION(doValidation: boolean) {
    this.form.doValidation = doValidation;
  }

  @Mutation
  RESET_INVITE_USER_MODAL_FORM() {
    this.form.doValidation = false;
    this.form.data.tenantUserInvite.resetForm();
    this.form.state.tenantUserInvite.resetState();
  }

  @Mutation
  SET_TENANT_USER_INVITE_ERROR(errorMessage: string): void {
    this.tenantUserInviteError = errorMessage;
  }

  // Actions
  @Action
  async getUsers(payload: { adminService: IAdminService, tenantId: string }) {

    AppStoreModule.SET_ISLOADING(true);

    await payload.adminService.getUsers(payload.tenantId)
      .then((users: TenantUser[]) => {
        this.SET_TENANT_USER(users);
      })
      .catch(() => {
        AppStoreModule.SAVE_HTTP_ERROR('Unable to load users');
      })
      .finally(() => {
        AppStoreModule.SET_ISLOADING(false);
      });
  }

  @Action
  setFilter(filter: string) {
    this.SET_FILTER(filter);
  }

  @Action
  setCurrentPage(currentPage: number) {
    this.SET_CURRENT_PAGE(currentPage);
  }

  @Action
  async inviteUserModalFormInputChange(payload: { fieldName: string, value: any }) {
    this.UPDATE_INVITE_USER_FORM_FIELD_BY_NAME(payload);
    await this.UPDATE_INVITE_USER_FORM_STATE();
  }

  @Action
  async resetInviteUserModalForm() {
    this.SET_TENANT_USER_INVITE_ERROR('');
    this.RESET_INVITE_USER_MODAL_FORM();
  }

  @Action
  async inviteUserToTenancy(payload: { adminService: IAdminService, tenantId: string }): Promise<boolean> {
    let isSuccessful: boolean = false;

    if (!this.form.doValidation) {
      this.SET_INVITE_USER_FORM_DO_VALIDATION(true);
      await Promise.all([
        this.UPDATE_INVITE_USER_FORM_STATE(),
      ]);
    }

    if (this.isInviteUserFormValid) {
      AppStoreModule.SET_ISLOADING(true);

      try {
        const tenantUser: TenantUser = UsersHelper.getTenantUser(this.form.data.tenantUserInvite);
        await payload.adminService.inviteTenantUser(payload.tenantId, tenantUser);
        isSuccessful = true;
      } catch (error) {
        AppStoreModule.SAVE_HTTP_ERROR('Unable to invite user');
      } finally {
        AppStoreModule.SET_ISLOADING(false);
      }
    }
    return isSuccessful;
  }

  @Action
  updateTenantUserInviteError(errorMessage: string): void {
    this.SET_TENANT_USER_INVITE_ERROR(errorMessage);
  }

  @Action
  async selfInvite(payload: { adminService: IAdminService, tenantId: string }): Promise<boolean> {
    AppStoreModule.SET_ISLOADING(true);
    AppStoreModule.CLEAR_HTTP_ERROR();
    let isSuccessful: boolean = false;
    try {
      await payload.adminService.selfInvite(payload.tenantId);
      isSuccessful = true;
    } catch (error) {
      AppStoreModule.SAVE_HTTP_ERROR('Unable to complete self invite.');
    } finally {
      AppStoreModule.SET_ISLOADING(false);
    }
    return isSuccessful;
  }
}
